import { Component } from '@theme/component';
import {
  getScrollTop,
  getScrollEventTarget,
  getScrollContainer,
  scrollTo,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const STORAGE_KEY = 'drip-noir-theme';
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * 24;
const SCROLL_SHOW_THRESHOLD = 0.05;
const BOTTOM_THRESHOLD = 0.9;

/**
 * @returns {'light' | 'dark'}
 */
function getStoredTheme() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch (_) {
    /* ignore */
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * @param {'light' | 'dark'} theme
 */
function applyTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.setAttribute('data-rz-mode', theme);
  document.documentElement.classList.toggle('rz-light-mode', theme === 'light');
  document.querySelectorAll('.rz-header').forEach(function (el) {
    el.setAttribute('data-rz-mode', theme);
  });
  try {
    localStorage.setItem(STORAGE_KEY, theme);
    localStorage.setItem('rz-mode', theme);
    localStorage.setItem('rz-theme', theme);
  } catch (_) {
    /* ignore */
  }

  const metaTheme = document.querySelector('meta[name="theme-color"]');
  if (metaTheme instanceof HTMLMetaElement) {
    metaTheme.content = theme === 'dark' ? '#000000' : '#ffffff';
  }

  document.dispatchEvent(new CustomEvent('rz:theme:change', { detail: { mode: theme } }));
  document.dispatchEvent(new CustomEvent('rz:mode:change', { detail: { mode: theme } }));
  document.dispatchEvent(new CustomEvent('theme:change', { detail: { theme } }));
}

/**
 * @returns {{ scrollTop: number, scrollHeight: number, clientHeight: number, progress: number, atBottom: boolean }}
 */
function getScrollMetrics() {
  const container = getScrollContainer();
  const scrollTop = getScrollTop();
  const scrollHeight = container.scrollHeight;
  const clientHeight = container.clientHeight;
  const maxScroll = Math.max(scrollHeight - clientHeight, 1);
  const progress = Math.min(Math.max(scrollTop / maxScroll, 0), 1);
  const fromEnd = maxScroll - scrollTop;
  // px-based bottom so iOS/safe-area doesn't trap users at 97%
  const atBottom = fromEnd <= 120 || progress >= BOTTOM_THRESHOLD;

  return { scrollTop, scrollHeight, clientHeight, progress, atBottom };
}

class ThemeToggle extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#toggle);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#toggle);
  }

  #toggle = () => {
    const current = document.documentElement.getAttribute('data-theme') || 'light';
    applyTheme(current === 'dark' ? 'light' : 'dark');
  };
}

/**
 * @typedef {Object} ScrollProgressButtonRefs
 * @property {SVGCircleElement} progressCircle
 * @property {HTMLElement} label
 * @property {HTMLElement} arrow
 * @property {HTMLButtonElement} button
 */

/**
 * @extends {Component<ScrollProgressButtonRefs>}
 */
class ScrollProgressButton extends Component {
  /** @type {EventTarget | null} */
  #scrollTarget = null;

  /** @type {number | null} */
  #rafId = null;

  connectedCallback() {
    super.connectedCallback();
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#handleScroll, { passive: true });
    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    this.refs.button?.addEventListener('click', this.#scrollToTop);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.refs.button?.removeEventListener('click', this.#scrollToTop);
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  #handleBreakpointChange = () => {
    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#update();
  };

  #handleScroll = () => {
    if (this.#rafId !== null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  #scrollToTop = () => {
    scrollTo({ top: 0, behavior: 'smooth' });
  };

  #update() {
    const { progress, atBottom } = getScrollMetrics();
    const percent = Math.round(progress * 100);
    const visible = progress > SCROLL_SHOW_THRESHOLD;

    this.dataset.visible = visible ? 'true' : 'false';
    this.dataset.atBottom = atBottom ? 'true' : 'false';
    this.hidden = !visible;

    if (this.refs.label) {
      this.refs.label.textContent = `${percent}%`;
    }

    if (this.refs.progressCircle) {
      const offset = CIRCLE_CIRCUMFERENCE * (1 - progress);
      this.refs.progressCircle.style.strokeDashoffset = String(offset);
    }

    if (this.refs.button) {
      this.refs.button.setAttribute(
        'aria-label',
        atBottom ? 'Scroll to top' : `${percent}% scrolled`
      );
    }

    this.#updateHeaderProgress(progress);
  }

  /**
   * @param {number} progress
   */
  #updateHeaderProgress(progress) {
    const header = document.querySelector('#header-component');
    if (!(header instanceof HTMLElement)) return;

    const bar = header.querySelector('.header__scroll-progress');
    if (bar instanceof HTMLElement) {
      bar.style.width = `${progress * 100}%`;
    }

    header.dataset.scrolled = progress > 0.01 ? 'true' : 'false';
  }
}

if (!customElements.get('theme-toggle')) {
  customElements.define('theme-toggle', ThemeToggle);
}

if (!customElements.get('scroll-progress-button')) {
  customElements.define('scroll-progress-button', ScrollProgressButton);
}

const initialTheme = document.documentElement.getAttribute('data-theme');
if (initialTheme === 'dark' || initialTheme === 'light') {
  applyTheme(initialTheme);
} else {
  applyTheme(getStoredTheme());
}

/**
 * Custom float header for legacy Horizon header only.
 * Skip when REZ rz-header is present.
 */
(function initFloatingHeader() {
  if (document.querySelector('.rz-header')) return;

  const HIDE_AFTER = 80;
  const TOP_THRESHOLD = 16;

  /** @type {HTMLElement | null} */
  let group = null;
  /** @type {EventTarget | null} */
  let scrollTarget = null;
  /** @type {number} */
  let lastScrollTop = 0;
  /** @type {number | null} */
  let rafId = null;
  /** @type {'top' | 'hidden' | 'stuck'} */
  let state = 'top';

  function bind() {
    group = document.querySelector('#header-group');
    if (!group) return;

    group.dataset.floatState = 'top';
    state = 'top';
    lastScrollTop = getScrollTop();

    scrollTarget?.removeEventListener('scroll', onScroll);
    scrollTarget = getScrollEventTarget();
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    scrollContainerMediaQuery.addEventListener('change', onBreakpoint);
    update();
  }

  function onBreakpoint() {
    scrollTarget?.removeEventListener('scroll', onScroll);
    scrollTarget = getScrollEventTarget();
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    lastScrollTop = getScrollTop();
    update();
  }

  function onScroll() {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      update();
    });
  }

  /**
   * @param {'top' | 'hidden' | 'stuck'} next
   */
  function setState(next) {
    if (!group || state === next) return;
    state = next;
    group.dataset.floatState = next;
  }

  function update() {
    if (!group) return;

    const scrollTop = getScrollTop();
    const scrollingUp = scrollTop < lastScrollTop;
    const scrollingDown = scrollTop > lastScrollTop;

    if (scrollTop <= TOP_THRESHOLD) {
      setState('top');
    } else if (scrollingUp) {
      setState('stuck');
    } else if (scrollingDown && scrollTop > HIDE_AFTER) {
      setState('hidden');
    }

    lastScrollTop = scrollTop;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
